import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Grid from "@mui/material/Grid";
import { Button, Typography } from "@mui/material";
import { Box } from "@mui/system";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ButtonGroup from "@mui/material/ButtonGroup";
import { map, sortBy, find } from "lodash";

import AssetList from "../../Components/List/SelectionList";
import Confirmation from "../../Components/Modal/Confirmation";
import {
  getAssignmentInfo,
  getAssignmentLearners,
  updateAssignment,
} from "./Services";
import { getFormatedDate, getFormatedTime } from "../../Js/TimeAndDate";
import { assignmentSliceActions } from "../../Store/Slices/AssignSlice";
import { userLogs, durationCalculate, getClassNames } from "../../Js/Common";

const AssignInfo = (props) => {
  // Hooks
  const navigation = useNavigate();
  const dispatch = useDispatch();
  let { id } = useParams();

  // State
  const [assignmentInfo, setAssignmentInfo] = useState(null);
  const [learnerList, setLearnerList] = useState(null);
  const [confirmModal, setConfirmModal] = useState(false);
  const [startTime, setStartTime] = useState(0);
  const [classes, setClasses] = useState([]);

  // Redux state
  const userInfo = useSelector((state) => state.loggedInUserInfo.userInfo);

  // React logic hooks
  useEffect(() => {
    if (userInfo) {
      const innerFunction = async () => {
        setClasses(await getClassNames(userInfo.site_uuid));
      };
      innerFunction();
    }
  }, [userInfo]);

  useEffect(() => {
    fetchAssignmentInfo();
    dispatch(assignmentSliceActions.resetState());
    dispatch(assignmentSliceActions.setDurationFlag(false));
    setStartTime(Date.now());
  }, [classes]);

  // Fetch Assignment Information
  const fetchAssignmentInfo = async () => {
    let assignmentInfoData = await getAssignmentInfo(id, userInfo.site_uuid);
    setAssignmentInfo(assignmentInfoData.data[0]);
    let learners = await getAssignmentLearners(
      assignmentInfoData.data[0].learners.learners,
      userInfo.site_uuid,
      [true, false]
    );
    setLearnerList(
      sortBy(withClassName(learners.data), [
        "classname",
        (o) => {
          return o.name.firstname;
        },
      ])
    );
  };

  // To drop logs
  const logFunction = async (action, duration) => {
    let logResponse = await userLogs({
      actor: userInfo.uid,
      verb: "assignment",
      target: assignmentInfo.uid,
      outcome: {
        value: "",
        action: action,
        duration: duration,
      },
      site_uuid: userInfo.site_uuid,
    });
  };

  const withClassName = (learners) => {
    return learners.map((val) => {
      return {
        ...val,
        classname: className(val.belongs_to),
      };
    });
  };

  const className = (id) => {
    console.log("classes", classes);
    let foundObj = find(classes, { id: id });
    if (foundObj) {
      return foundObj.value;
    }
    return "NA";
  };

  // Delete Action
  const deleteConfirm = async () => {
    let response = await updateAssignment(
      {
        is_deleted: true,
      },
      assignmentInfo.uid,
      userInfo.site_uuid
    );
    if (response.status == 200) {
      setConfirmModal(false);
      logFunction("delete", 0);
      navigation("/Assign");
    }
  };

  // Edit Action
  const edit = () => {
    navigation(`/Assign/Edit/${assignmentInfo.uid}`);
  };

  return (
    <Grid container>
      <Grid item xs={12} sx={{}}>
        <Grid
          container
          sx={{
            flexDirection: "row",
            justifyContent: "space-around",
            pt: 2,
            pl: 3,
            pr: 7,
            backgroundColor: "#F5F7FF",
            overflow: "auto",
            width: "95%",
            marginLeft: 4,
            borderRadius: 5,
          }}
        >
          <Grid container xs={9} sx={{}}>
            {assignmentInfo && (
              <Box>
                <Typography
                  color="primary.main"
                  sx={{
                    lineHeight: 2,
                    "@media (min-width: 1636px)": {
                      fontSize: "1.4rem",
                    },
                  }}
                >
                  <b>Name: </b>{" "}
                  {assignmentInfo.title ? assignmentInfo.title : ""}
                </Typography>
                <Typography
                  color="primary.main"
                  sx={{
                    lineHeight: 2,
                    "@media (min-width: 1636px)": {
                      fontSize: "1.4rem",
                    },
                  }}
                >
                  <b>Description:</b>{" "}
                  {assignmentInfo.description ? assignmentInfo.description : ""}
                </Typography>
                <Typography
                  color="primary.main"
                  sx={{
                    lineHeight: 2,
                    "@media (min-width: 1636px)": {
                      fontSize: "1.4rem",
                    },
                  }}
                >
                  <b>Expiry:</b>{" "}
                  {assignmentInfo.expiries
                    ? getFormatedDate(
                        assignmentInfo.expiries,
                        "DD MMM YY | HH:mm"
                      )
                    : ""}
                </Typography>
                <Typography
                  color="primary.main"
                  sx={{
                    lineHeight: 2,
                    "@media (min-width: 1636px)": {
                      fontSize: "1.4rem",
                    },
                  }}
                >
                  <b>Attempts:</b>{" "}
                  {assignmentInfo.defaults
                    ? assignmentInfo.defaults.defaults.noofattempts
                    : ""}
                </Typography>
                <Typography
                  color="primary.main"
                  sx={{
                    lineHeight: 2,
                    "@media (min-width: 1636px)": {
                      fontSize: "1.4rem",
                    },
                  }}
                >
                  <b>Type:</b> {assignmentInfo.is_exam ? "E-EXAM" : "Normal"}
                </Typography>
              </Box>
            )}
          </Grid>
          <Grid container xs={3} sx={{ justifyContent: "right" }}>
            <Box
              sx={{
                display: "flex",
                "& > *": {
                  m: 1,
                  pl: 5,
                },
              }}
            >
              <Typography
                color="primary.main"
                sx={{
                  lineHeight: 2,
                  "@media (min-width: 1636px)": {
                    fontSize: "1.4rem",
                  },
                }}
              >
                {assignmentInfo && assignmentInfo.estimated_time && (
                  <b>
                    {`${getFormatedTime(
                      assignmentInfo.estimated_time,
                      "HH:mm:ss"
                    )} Hrs`}
                  </b>
                )}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                "& > *": {
                  my: 3,
                  mt: 10,
                },
              }}
            >
              <ButtonGroup
                variant="text"
                sx={{ backgroundColor: "#FFFFFF", borderRadius: 15 }}
              >
                <Button sx={{ px: 2 }}>
                  <BorderColorOutlinedIcon
                    onClick={edit}
                    sx={{
                      "@media (min-width: 1636px)": {
                        fontSize: "2rem",
                      },
                    }}
                  />
                </Button>
                <Button sx={{ px: 2 }} onClick={() => setConfirmModal(true)}>
                  <DeleteOutlineIcon
                    sx={{
                      color: "#c40000",
                      "@media (min-width: 1636px)": {
                        fontSize: "2rem",
                      },
                    }}
                  />
                </Button>
              </ButtonGroup>
            </Box>
          </Grid>
        </Grid>
        <Grid sx={{ pt: 3, pl: 3, pr: 7 }}>
          {/* <Grid container xs={12} justifyContent="end">
            <Button
              variant="contained"
              size="small"
              sx={{ px: 4, borderRadius: 15 }}
            >
              Assign Task
            </Button>
          </Grid> */}
          <Box>
            <Typography
              color="primary.main"
              sx={{
                pl: 3,
                "@media (min-width: 1636px)": {
                  fontSize: "1.4rem",
                },
              }}
            >
              <b>Learners</b>
            </Typography>
            {learnerList &&
              learnerList.map((val, index) => {
                return (
                  <div key={`${index}_assets`}>
                    <AssetList
                      title={`${index + 1}. ${val.name.firstname} ${
                        val.name.lastname
                      } - (${val.classname})`}
                      viewOnly={true}
                      deleted={!val.status}
                    ></AssetList>
                  </div>
                );
              })}
          </Box>
        </Grid>
        <Confirmation
          image="delete"
          primaryText="Delete Assignment"
          secondaryText="Do you want to delete this assignment ?"
          visible={confirmModal}
          positiveClick={deleteConfirm}
          negativeClick={() => {
            setConfirmModal(false);
          }}
          toggleModal={(flag) => setConfirmModal(flag)}
        ></Confirmation>
      </Grid>
    </Grid>
  );
};

export default AssignInfo;
